import { useEffect, useLayoutEffect, useRef, useState } from "react";

// jsdom/SSR-safe layout effect (matches the Hero pattern): measure before the
// first client paint so the un-fitted font-size guess is never visible.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Faint, oversized "ghost" watermark of the business name. Static, centered in
// whatever section hosts it, clipped by that section's overflow — it scrolls
// away with the section, never moves on its own (no scroll-driven motion).
//
// Sizing: instead of a per-character estimate we measure the rendered text
// width and scale linearly to the container — every dimension of the text
// (glyph widths AND the em-based letter-spacing) scales proportionally with
// font size, so `nextSize = currentSize × target/measured` is an exact
// one-step fit. Short names stay capped at the dramatic ceiling (~28% of the
// container width, hard-capped at 26rem); the fit is authoritative (no lower
// bound) so the text NEVER overflows regardless of length.
export default function GhostWatermark({
  name,
  logoUrls,
  tagline,
  fontFamily,
  brandOpacity = 50,
  taglineOpacity = 50,
  forceVisible = false,
}: {
  name: string;
  logoUrls?: string[];
  // Optional owner tagline (business_profile.tagline) rendered as a small
  // watermark-styled line under the faded logo / ghost word (blank = none).
  tagline?: string;
  /** Optional dashboard preview override resolved from the saved Hero headline. */
  fontFamily?: string;
  brandOpacity?: number;
  taglineOpacity?: number;
  /** Editor swatches are thumbnail-sized but still need to show the mark on mobile. */
  forceVisible?: boolean;
}) {
  const ghostName = name.replace(/\.+$/, "").trim();
  const ghostTagline = (tagline ?? "").trim();
  const logoCandidates = (logoUrls ?? []).map((u) => u.trim()).filter(Boolean);
  const brandIntensity = Math.min(100, Math.max(0, brandOpacity));
  const taglineIntensity = Math.min(100, Math.max(0, taglineOpacity));
  const brandInk = `color-mix(in srgb, var(--theme-cream-bg, #000000) ${100 - brandIntensity * 0.24}%, var(--footer-fg, var(--theme-footer-muted, #EAEAEA)))`;
  const taglineInk = `color-mix(in srgb, var(--theme-cream-bg, #000000) ${100 - taglineIntensity * 0.24}%, var(--footer-fg, var(--theme-footer-muted, #EAEAEA)))`;
  // The first candidate that actually LOADS (stale CDN urls 404 — the mask
  // would silently render nothing). null = still probing (or no candidates);
  // "" = all candidates failed, fall back to the ghost word.
  const [ghostLogo, setGhostLogo] = useState<string | null>(logoCandidates.length ? null : "");
  const candidatesKey = logoCandidates.join("|");

  useEffect(() => {
    if (!logoCandidates.length) {
      setGhostLogo("");
      return;
    }
    let cancelled = false;
    (async () => {
      for (const url of logoCandidates) {
        const ok = await new Promise<boolean>((resolve) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = url;
        });
        if (cancelled) return;
        if (ok) {
          setGhostLogo(url);
          return;
        }
      }
      if (!cancelled) setGhostLogo("");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesKey]);
  const containerRef = useRef<HTMLDivElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState<number | null>(null);

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    const span = spanRef.current;
    if (!container || !span || !ghostName) return;

    const measure = () => {
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const measuredWidth = span.getBoundingClientRect().width;
      if (!containerWidth || !measuredWidth) return;
      const currentSize = parseFloat(getComputedStyle(span).fontSize) || 1;
      const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      // Fit to ~96% of the container width. Cap (but never floor) at the
      // dramatic ceiling so short names stay big; long names shrink as far as
      // needed to never overflow. Also cap by the host section's HEIGHT
      // (leading-none, so glyph height ≈ font size) — short bands like the
      // footer would otherwise clip the letters to a horizontal slice.
      const fitted = currentSize * ((containerWidth * 0.96) / measuredWidth);
      const maxPx = Math.min(containerWidth * 0.28, 26 * remPx);
      // Reserve vertical room for the optional tagline line (its font is
      // ~14% of the name's + an 8px margin), so the stacked pair never gets
      // clipped by the section's overflow in short bands like the footer.
      const heightBudget = ghostTagline ? 0.72 : 0.9;
      const heightCap = containerHeight ? containerHeight * heightBudget : Infinity;
      const next = Math.min(fitted, maxPx, heightCap);
      setFontSize((prev) => (prev !== null && Math.abs(prev - next) < 0.5 ? prev : next));
    };

    measure();

    let cancelled = false;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measure()) : null;
    ro?.observe(container);
    // Custom/serif webfonts change text metrics; re-measure once they load.
    document.fonts?.ready?.then(() => {
      if (!cancelled) measure();
    });

    return () => {
      cancelled = true;
      ro?.disconnect();
    };
    // ghostLogo is a dep because the span only exists once probing settles on
    // the word path ("") — without it the measurement never runs post-probe.
  }, [ghostName, ghostLogo]);

  // Still probing the logo candidates — render nothing rather than flashing
  // the ghost word and swapping it for the logo a beat later.
  if (ghostLogo === null) return null;

  // Ghost LOGO variant (July 2026 experiment): when the owner has a loadable
  // logo, show it — as big as the hosting section allows — instead of the
  // ghost word. Drop the `logoUrls` prop at the call site to revert.
  if (ghostLogo) {
    return (
      <div
        aria-hidden="true"
        className={`${forceVisible ? "flex" : "hidden sm:flex"} absolute inset-0 z-0 items-center justify-center pointer-events-none select-none overflow-hidden`}
        data-testid="text-ghost-name"
      >
        {/* Faded ACTUAL logo (July 2026, replaced the one-color mask
            silhouette): the real image at very low opacity keeps the logo's
            full detail recognizable. Solid-badge logos read as blobs in
            silhouette form, which owners mistook for "nothing changed". */}
        <div className="flex h-full w-full flex-col items-center justify-center">
          <img
            src={ghostLogo}
            alt=""
            data-testid="watermark-business-logo"
            className={`w-[96%] object-contain ${ghostTagline ? "h-[72%]" : "h-[90%]"}`}
            style={{ opacity: 0.3 * (brandIntensity / 100) }}
          />
          {ghostTagline && (
            <span
              data-testid="watermark-tagline"
              className="mt-2 font-serif font-semibold uppercase tracking-[0.18em] whitespace-nowrap max-w-[96%] overflow-hidden text-ellipsis"
              style={{ fontSize: "clamp(13px, 1.6vw, 22px)", color: taglineInk }}
            >
              {ghostTagline}
            </span>
          )}
        </div>
      </div>
    );
  }

  if (!ghostName) return null;

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={`${forceVisible ? "flex" : "hidden sm:flex"} absolute inset-0 z-0 items-center justify-center pointer-events-none select-none overflow-hidden`}
      data-testid="text-ghost-name"
    >
      <div className="flex flex-col items-center">
      <span
        ref={spanRef}
        data-testid="watermark-business-name"
        data-headline-font={fontFamily ?? "template-default"}
        className="font-serif font-extrabold uppercase whitespace-nowrap leading-none tracking-[-0.04em]"
        style={{
          fontSize: fontSize !== null ? `${fontSize}px` : "10vw",
          ...(fontFamily ? { fontFamily } : {}),
          // A light tint of the hosting section's background (the footer,
          // --theme-cream-bg) rather than the accent color: mixing a sliver of
          // the footer's text color into the background nudges it just enough
          // to read as a subtle same-hue watermark on ANY background — lighter
          // on dark designs, a shade deeper on light ones. Mix eased 93% → 88%
          // (July 2026) so the letters show appropriately on the pure-black
          // default footer.
          color: brandInk,
          // Invisible until the fit measurement lands (the layout effect runs
          // before the first client paint), then fades in at its final size —
          // the un-measured 10vw guess is never visible, so the watermark never
          // visibly resizes. Purely decorative, so briefly absent on the SSR
          // paint is fine; opacity-only, no layout shift.
          opacity: fontSize !== null ? 1 : 0,
          transition: "opacity 0.5s ease",
        }}
      >
        {ghostName}
      </span>
      {ghostTagline && (
        <span
          data-testid="watermark-tagline"
          className="mt-2 font-serif font-semibold uppercase tracking-[0.18em] whitespace-nowrap max-w-full overflow-hidden text-ellipsis"
          style={{
            fontSize: fontSize !== null ? `${Math.max(13, fontSize * 0.14)}px` : "1.4vw",
            color: taglineInk,
            opacity: fontSize !== null ? 1 : 0,
            transition: "opacity 0.5s ease",
          }}
        >
          {ghostTagline}
        </span>
      )}
      </div>
    </div>
  );
}
